<template>
  <div>
    <b-table
      striped
      hover
      show-empty
      primary-key="id"
      :fields="fields"
      :selectable="isSelectable"
      :select-mode="'multi'"
      :selected="selectedVersions"
      selected-variant="info"
      :items="list"
      :current-page="currentPage"
      :per-page="perPage"
      @row-selected="onVersionSelect">
      <template #empty>
        <div class="flex-center p-5">No items.</div>
      </template>
      <template #cell(id)="data">
        <span v-b-tooltip.hover :title="data.item.filename">
          {{ data.value }}
        </span>
      </template>
      <template #cell(selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <b-icon-check-square-fill></b-icon-check-square-fill>
        </template>
        <template v-else>
          <b-icon-square></b-icon-square>
        </template>
      </template>
      <template #cell(description)="data">
        <b-badge
          v-if="data.value"
          variant="light"
          v-b-popover.hover.top="data.value"
          :title="data.item.version"
          class="text-truncate mt-1"
          style="max-width: 200px">
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(options)="data">
        <b-popover
          v-if="data.value"
          :target="`popover_options__${data.item._id}`"
          placement="top"
          triggers="hover focus">
          <template #title>
            <span class="font-weight-bold">Options</span>
          </template>
          <div class="d-flex flex-column">
            <div class="d-flex justify-content-between">
              <div class="mr-2">Delete questions:</div>
              <div v-if="data.value.removeCustomQuestions">{{ data.value.removeCustomQuestions.length }} questions</div>
              <div v-else>None</div>
            </div>
          </div>
        </b-popover>
        <b-badge
          v-if="data.value"
          variant="success"
          :id="`popover_options__${data.item._id}`"
          class="text-truncate mt-1"
          style="max-width: 200px">
          Yes
        </b-badge>
        <b-badge
          v-else
          variant="light"
          :id="`popover_options__${data.item._id}`"
          class="text-truncate mt-1"
          style="max-width: 200px">
          No
        </b-badge>
      </template>
      <template #cell(type)="data">
        <b-badge v-if="data.value == 'package'" variant="info">{{ capitalize(data.value) }}</b-badge>
        <b-badge v-else variant="success">{{ capitalize(data.value) }}</b-badge>
      </template>
      <template #cell(language)="data">
        <span>{{ languagesSelect[data.value] || data.value || '' }}</span>
      </template>
      <template #cell(preRelease)="data">
        <b-badge v-if="data.value == true" variant="warning">Pre Release</b-badge>
        <b-badge v-else variant="light">Stable</b-badge>
      </template>
      <template #cell(enabled)="data">
        <b-badge v-if="data.value == true" variant="success">Yes</b-badge>
        <b-badge v-else variant="light">No</b-badge>
      </template>
      <template #cell(createdAt)="data">
        {{ data.value | formatDate }}
      </template>
      <template #cell(actions)="data">
        <div class="d-flex justify-content-end">
          <!-- <b-button v-if="data.item.enabled == true" size="sm" variant="warning" @click="disableVersion(data.item)">Désactiver</b-button>
                    <b-button v-else type="button" size="sm" variant="success" @click="enableVersion(data.item)">Activer</b-button> -->
          <b-button size="sm" variant="danger" class="ml-1" @click="destroyVersion(data.item)">Delete</b-button>
          <b-button size="sm" variant="primary" class="mx-1" @click="showContent(data.item)">View content</b-button>
          <b-button
            v-if="data.item.type == 'data' && data.item.preRelease == true && showDiffBtn"
            size="sm"
            variant="primary"
            class="mx-1"
            @click="showDiff(data.item)">
            View diff
          </b-button>
          <b-button size="sm" variant="info" class="ml-1" @click="downloadVersion(data.item)">Download</b-button>
        </div>
      </template>
    </b-table>

    <div class="d-inline-block d-sm-flex justify-content-between mt-3">
      <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"></b-pagination>

      <b-form-group
        class=""
        label="Per page"
        label-cols-sm="6"
        label-cols-lg="6"
        content-cols-sm="6"
        content-cols-lg="6">
        <b-form-select v-model="perPage" class="ml-2">
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </b-form-select>
      </b-form-group>
    </div>

    <b-modal id="zip-content-modal" title="ZIP content" :hide-footer="true" ref="zip-content-modal">
      <b-spinner v-if="zipContentLoading" variant="primary" label="Spinning"></b-spinner>
      <ul>
        <li v-for="file in zipContent" :key="file">{{ file }}</li>
      </ul>
    </b-modal>

    <b-modal id="diff-version-modal" title="Differences" :hide-footer="true" ref="diff-version-modal" size="xl">
      <b-spinner v-if="diffVersionLoading" variant="primary" label="Spinning"></b-spinner>
      <div v-else>
        <ContentDiffStats
          v-if="!diffVersionLoading && versionsDiff"
          title="Edits"
          :created-questions="versionsDiff.questions.created.length"
          :updated-questions="versionsDiff.questions.updated.length"
          :deleted-questions="versionsDiff.questions.deleted.length"
          :created-games="versionsDiff.games.created.length"
          :updated-games="versionsDiff.games.updated.length"
          :deleted-games="versionsDiff.games.deleted.length"
          @show-details="openContentDiffStatsModal" />
      </div>
    </b-modal>

    <!-- Modal pour afficher les détails des différences -->
    <b-modal v-model="showContentDiffStatsModal" :title="contentDiffStatsModalTitle" :hide-footer="true" size="xl">
      <b-spinner v-if="contentDiffLoading" variant="primary" label="Spinning"></b-spinner>
      <div v-else-if="currentVersionDiff">
        <!-- Questions -->
        <div v-if="contentDiffType === 'questions'">
          <div v-for="question in paginatedItems" :key="'q__' + question.id" class="mb-3 p-3 border rounded">
            <QuestionContentResume
              :key="'q-resume__' + question._id"
              :title="contentDiffElementTitle"
              :variant="contentDiffElementVariant"
              :title-icon="contentDiffElementTitleIcon"
              :question="question" />
          </div>
        </div>

        <!-- Games -->
        <div v-if="contentDiffType === 'games'">
          <div v-for="game in paginatedItems" :key="'g__' + game.id" class="mb-3 p-3 border rounded">
            <GameContentResume
              :key="'g-resume__' + game._id"
              :title="contentDiffElementTitle"
              :variant="contentDiffElementVariant"
              :title-icon="contentDiffElementTitleIcon"
              :game="game" />
          </div>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-center mt-4" v-if="totalPages > 1">
          <b-pagination
            v-model="currentDiffPage"
            :total-rows="totalDiffItems"
            :per-page="perDiffPage"
            align="center"
            size="md"
            class="my-0"></b-pagination>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import { capitalize } from 'lodash'
  import ContentDiffStats from './ContentDiffStats.vue'
  import QuestionContentResume from './question/QuestionContentResume.vue'
  import GameContentResume from './game/GameContentResume.vue'
  import versionService from '@/services/versionService'

  export default {
    name: 'VersionsTable',
    components: {
      ContentDiffStats,
      QuestionContentResume,
      GameContentResume,
    },
    props: {
      list: {
        type: Array,
        required: true,
      },
      fieldsOnly: {
        type: Array,
        required: false,
        default: () => [],
      },
      showDiffBtn: {
        type: Boolean,
        required: false,
        default: false,
      },
      generalFilters: {
        type: Object,
        default: () => ({
          versions: [],
          language: '',
        }),
      },
    },
    computed: {
      languagesSelect() {
        return {
          all: 'All',
          en: 'English',
          us: 'English - US',
          es: 'Spanish',
          fr: 'French',
          nl: 'Netherlands',
          ge: 'German',
          du: 'Dutch',
          hu: 'Hungarian',
          pt: 'Portugal',
          showroom: 'showroom',
        }
      },
      fields() {
        const defaultFields = [
          { key: 'selected', label: '', selectable: true },
          { key: 'id', label: 'ID', sortable: true },
          { key: 'type', label: 'Type', sortable: true },
          { key: 'language', label: 'Language', sortable: true },
          { key: 'version', label: 'Version', sortable: true },
          { key: 'numberVersion', label: 'Number', sortable: true },
          { key: 'preRelease', label: 'Pre Release', sortable: true },
          { key: 'enabled', label: 'Enabled', sortable: true },
          { key: 'description', label: 'Description' },
          { key: 'options', label: 'Options' },
          { key: 'createdAt', label: 'Created on', sortable: true },
          { key: 'actions', label: '' },
        ]

        const fields =
          this.fieldsOnly.length > 0
            ? this.fieldsOnly.map((field) => defaultFields.find((f) => f.key === field))
            : defaultFields

        return fields
      },
      totalRows() {
        return this.list.length
      },
      isSelectable() {
        return this.fields.some((field) => field.key === 'selected')
      },
      paginatedItems() {
        if (!this.currentVersionDiff || !this.contentDiffType || !this.contentDiffStatus) {
          return []
        }
        const items = this.currentVersionDiff[this.contentDiffType][this.contentDiffStatus]
        const start = (this.currentDiffPage - 1) * this.perDiffPage
        const end = start + this.perDiffPage
        return items.slice(start, end)
      },
      totalDiffItems() {
        if (!this.currentVersionDiff || !this.contentDiffType || !this.contentDiffStatus) {
          return 0
        }
        return this.currentVersionDiff[this.contentDiffType][this.contentDiffStatus].length
      },
      totalPages() {
        return Math.ceil(this.totalDiffItems / this.perDiffPage)
      },
    },
    data() {
      return {
        currentPage: 1,
        perPage: 10,
        selectedVersions: [],
        zipContent: null,
        zipContentLoading: false,
        zipContentLoadingError: null,
        diffVersionLoading: false,
        versionsDiff: null,
        // Modal des détails de diff
        showContentDiffStatsModal: false,
        contentDiffStatsModalTitle: null,
        contentDiffLoading: false,
        contentDiffType: null,
        contentDiffStatus: null,
        currentVersionDiff: null,
        contentDiffElementTitle: null,
        contentDiffElementVariant: null,
        contentDiffElementTitleIcon: null,
        currentDiffPage: 1,
        perDiffPage: 10,
      }
    },
    methods: {
      capitalize(string) {
        return capitalize(string)
      },
      onVersionSelect(items) {
        this.selectedVersions = items
        this.$emit('selected', items)
      },
      async showContent(version) {
        this.zipContentLoading = true
        this.zipContentLoadingError = null
        this.zipContent = null

        this.$refs['zip-content-modal'].show()

        try {
          this.zipContent = await versionService.getVersionContent(version.id)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error loading content:', error)
          this.zipContentLoadingError = error.response?.data?.message || 'Unknown error'
        } finally {
          this.zipContentLoading = false
        }
      },
      async showDiff(version) {
        try {
          this.diffVersionLoading = true
          this.versionsDiff = null

          this.versionsDiff = await versionService.getDiff({
            language: this.generalFilters.language,
            versions: [version.numberVersion],
          })
          this.$refs['diff-version-modal'].show()
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error loading differences:', error)

          let errorMessage = 'Unknown error.'
          if (error.response) {
            errorMessage = error.response.data.message || error.response.data.msg || 'Unknown error'
          }

          this.$notify({
            type: 'error',
            title: 'Error loading differences',
            text: errorMessage,
          })
        } finally {
          this.diffVersionLoading = false
        }
      },
      async downloadVersion(version) {
        this.$notify({
          type: 'info',
          title: 'Download in progress...',
        })

        const url = await versionService.getDownloadUrl(version.id)

        // Download the file via the browser
        window.open(url, '_self')
      },
      async destroyVersion(version) {
        if (!confirm('Are you sure you want to delete version "' + version.type + ' v' + version.version + '"?')) {
          return
        }

        try {
          await versionService.deleteVersion(version.id)

          this.$notify({
            type: 'success',
            title: 'Deletion successful',
          })

          this.$emit('deleted', version)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error during deletion:', error)

          let errorMessage = 'Unknown error.'
          if (error.response) {
            errorMessage = error.response.data.message
          }

          this.$notify({
            type: 'error',
            title: 'Deletion error',
            text: errorMessage,
          })
        }
      },
      async openContentDiffStatsModal({ type, status }) {
        this.showContentDiffStatsModal = true
        this.contentDiffStatsModalTitle = `Content differences...`

        try {
          this.contentDiffLoading = true
          this.contentDiffType = type
          this.contentDiffStatus = status

          // Utiliser les données déjà chargées dans versionsDiff
          this.currentVersionDiff = this.versionsDiff
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error retrieving content differences:', error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to retrieve content differences',
          })
        } finally {
          this.contentDiffLoading = false
        }

        if (this.contentDiffType === 'questions') {
          if (this.contentDiffStatus === 'created') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.questions.created.length} Questions added`
            this.contentDiffElementTitle = 'Question added'
            this.contentDiffElementVariant = 'success'
            this.contentDiffElementTitleIcon = 'plus-circle-fill'
          } else if (this.contentDiffStatus === 'updated') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.questions.updated.length} Questions edited`
            this.contentDiffElementTitle = 'Question edited'
            this.contentDiffElementVariant = 'warning'
            this.contentDiffElementTitleIcon = 'pencil-fill'
          } else if (this.contentDiffStatus === 'deleted') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.questions.deleted.length} Questions deleted`
            this.contentDiffElementTitle = 'Question deleted'
            this.contentDiffElementVariant = 'danger'
            this.contentDiffElementTitleIcon = 'x-circle-fill'
          }
        } else if (this.contentDiffType === 'games') {
          if (this.contentDiffStatus === 'created') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.games.created.length} Games added`
            this.contentDiffElementTitle = 'Game added'
            this.contentDiffElementVariant = 'success'
            this.contentDiffElementTitleIcon = 'plus-circle-fill'
          } else if (this.contentDiffStatus === 'updated') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.games.updated.length} Games edited`
            this.contentDiffElementTitle = 'Game edited'
            this.contentDiffElementVariant = 'warning'
            this.contentDiffElementTitleIcon = 'pencil-fill'
          } else if (this.contentDiffStatus === 'deleted') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.games.deleted.length} Games deleted`
            this.contentDiffElementTitle = 'Game deleted'
            this.contentDiffElementVariant = 'danger'
            this.contentDiffElementTitleIcon = 'x-circle-fill'
          }
        }
      },
    },
  }
</script>
